/**
 * This file contains components to render existing Chat sessions in the sidepanel context.
 */
import {gql} from '@apollo/client';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CrumbLink} from 'ui-library/Breadcrumbs';
import Tooltip from 'ui-library/floating-ui/Tooltip';

import {ChatBreadcrumbQuery, ChatBreadcrumbQueryVariables} from './__generated/ChatBreadcrumbQuery';
import {getChatNameRenderInfo, NEW_CHAT_TEXT} from './ChatNameUtil';

export const CHAT_BREADCRUMB_QUERY = gql`
  query ChatBreadcrumbQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    chatSession(id: $id) {
      ... on ChatSessionNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function NewChatCrumbLink(): JSX.Element {
  return <CrumbLink>{NEW_CHAT_TEXT}</CrumbLink>;
}

export function ChatCrumbLink({chatSessionId}: {chatSessionId: string}): JSX.Element {
  const result = useBenchlingQuery<ChatBreadcrumbQuery, ChatBreadcrumbQueryVariables>(CHAT_BREADCRUMB_QUERY, {
    variables: {id: chatSessionId},
    renderCachedDataWhenRefetching: true,
  });
  return (
    <CrumbLink isLoading={result.type !== QueryResultType.SUCCESS || isInaccessible(result.data.chatSession)}>
      {(() => {
        if (result.type !== QueryResultType.SUCCESS || isInaccessible(result.data.chatSession)) {
          return '...';
        }
        const info = getChatNameRenderInfo(result.data.chatSession);
        return (
          <Tooltip text={info.text}>
            <>{info.node}</>
          </Tooltip>
        );
      })()}
    </CrumbLink>
  );
}
